/**
 * I passi del tutorial guidato dell'elenco Acquisti.
 *
 * Un solo preparativo: il menu della riga, che si apre nel `before` e si richiude
 * in `stopBoughtListTour`. I filtri stanno in pagina sopra la tabella e sono
 * sempre visibili.
 *
 * I bersagli sono scoped sulla pagina: la scheda di inserimento ha un tutorial suo
 * con gli stessi nomi di bersaglio.
 */
const at = name => `[data-tour-scope="bought-list"] [data-tour="${name}"]`;

/**
 * La voce del menu di riga si cerca senza scope.
 *
 * Vuetify stacca il contenuto dei menu e lo appende a `[data-app]`: da li' in poi
 * non e' piu' dentro la pagina, e un selettore con lo scope non lo troverebbe.
 * Non serve nemmeno: questo nome lo usa solo l'elenco.
 */
const ROW_DETAIL = '[data-tour="row-detail"]';

/** Oltre questo tempo si smette di aspettare che il menu si fermi. */
const SETTLE_TIMEOUT_MS = 1500;
const POLL_MS = 50;
const STILL_MS = 250;

/**
 * L'elemento, ma solo se e' davvero dentro la finestra.
 *
 * Serve al menu della riga: Vuetify lo lascia nel documento anche mentre si
 * chiude, e il controllo sulla sola presenza lo darebbe per buono.
 */
function onScreen(selector) {
	return () => {
		const element = document.querySelector(selector);

		if (!element || element.getClientRects().length === 0) {
			return null;
		}

		const content = element.closest('.v-menu__content');

		/* Un menu in chiusura resta a schermo, ma perde subito la classe di attivo. */
		if (content && !content.classList.contains('menuable__content__active')) {
			return null;
		}

		const box = element.getBoundingClientRect();
		const centerX = box.left + box.width / 2;

		if (centerX < 0 || centerX > window.innerWidth) {
			return null;
		}

		return element;
	};
}

function isOnScreen(selector) {
	return onScreen(selector)() !== null;
}

function position(selector) {
	const element = onScreen(selector)();

	if (!element) {
		return null;
	}

	const box = element.getBoundingClientRect();

	return `${Math.round(box.top)}:${Math.round(box.left)}`;
}

/** Aspetta che il bersaglio abbia smesso di muoversi: il menu entra con una transizione. */
async function waitStable(selector) {
	const deadline = performance.now() + SETTLE_TIMEOUT_MS;
	const needed = Math.ceil(STILL_MS / POLL_MS);
	let previous = null;
	let still = 0;

	while (performance.now() < deadline) {
		await new Promise(resolve => setTimeout(resolve, POLL_MS));

		const current = position(selector);

		still = current !== null && current === previous ? still + 1 : 0;
		previous = current;

		if (still >= needed) {
			return;
		}
	}
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * Il menu della riga si chiude al primo click fuori, e il click su "Avanti" e'
 * fuori: la chiusura e' stata accodata in cattura, prima ancora che il pulsante
 * facesse il suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openRowMenu() {
	await afterPendingClose();

	if (isOnScreen(ROW_DETAIL)) {
		return;
	}

	click(at('row-menu'));
	await waitStable(ROW_DETAIL);
}

/** Rimette la pagina com'era: menu della riga chiuso. */
export async function stopBoughtListTour() {
	await afterPendingClose();

	if (isOnScreen(ROW_DETAIL)) {
		click(at('row-menu'));
	}
}

export function createBoughtListTourSteps() {
	return [
		{
			body: "Questa pagina e' l'archivio delle fatture d'acquisto: una riga per ogni fattura elettronica di fornitore gia' registrata. Registrare un acquisto non e' solo archiviarlo - e' il gesto che carica la merce a magazzino e aggiorna i prezzi d'acquisto dei prodotti - quindi quello che vedi qui e' la storia di come si e' riempito il tuo magazzino.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Il piu' apre la pagina di inserimento: si sceglie il fornitore, si carica l'XML della fattura elettronica e si collegano le sue righe ai prodotti del catalogo. E' la parte che fa il lavoro vero, e ha un tutorial tutto suo: aprila e premi Tutorial guidato anche li'.",
			key: 'new',
			placement: 'bottom',
			target: at('new'),
			title: 'Registrare un acquisto',
		},
		{
			body: 'Sopra la tabella ci sono i filtri. Sono tre, e conviene sapere su cosa lavorano davvero prima di usarli - uno dei tre non cerca quello che ci si aspetta.',
			key: 'filters',
			placement: 'bottom',
			target: at('filters'),
			title: 'I filtri',
		},
		{
			body: "Attenzione a questo: Cerca non cerca il fornitore e nemmeno il numero della fattura. Cerca dentro le righe dell'acquisto, cioe' fra gli sku dei prodotti e i codici articolo del fornitore. Serve per rispondere a 'in quale fattura era entrato questo articolo?'. Si avvia con Invio o con la lente e funziona da quattro caratteri in su: sotto, la ricerca viene ignorata e l'elenco resta intero.",
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cerca: negli articoli',
		},
		{
			body: "Data ordine filtra per intervallo, e l'intervallo e' sulla data in cui l'acquisto e' stato registrato qui, non sulla data di emissione della fattura. Le due cose si somigliano ma non coincidono quasi mai: una fattura di gennaio caricata a marzo si trova cercando marzo. Si sceglie una data sola per avere tutto da li' in avanti, oppure due per un periodo chiuso.",
			key: 'date',
			placement: 'bottom',
			target: at('date'),
			title: 'Data ordine',
		},
		{
			body: "Fornitore filtra per il fornitore scelto al momento dell'inserimento - quello dell'anagrafica, non il nome che compare nell'XML. Se un acquisto non salta fuori filtrando per fornitore, e' il segno che al caricamento era stato scelto il fornitore sbagliato.",
			key: 'supplier',
			placement: 'bottom',
			target: at('supplier'),
			title: 'Fornitore',
		},
		{
			body: "Nelle colonne convivono due date: Data e' quando l'acquisto e' stato registrato qui, Data emissione e' quella scritta sulla fattura. Fornitore, Numero e Importo sono letti dall'XML, quindi sono quelli ufficiali del documento. ID Ordine invece non e' un progressivo: e' il codice del trasmittente preso dalla fattura elettronica, e si ripete uguale su tutte le fatture spedite dallo stesso intermediario.",
			before: stopBoughtListTour,
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "I tre pallini a inizio riga hanno una voce sola, Dettaglio, e apre la pagina della fattura in sola lettura: la testata, le righe che erano state spuntate al caricamento - solo quelle, le altre non sono mai state salvate - e il riepilogo IVA con aliquote, imponibili e imposta. Un acquisto registrato non si modifica da qui: se c'e' un errore va rifatto, tenendo presente che ricaricarlo somma di nuovo le quantita' a magazzino.",
			before: openRowMenu,
			key: 'row-detail',
			placement: 'right',
			target: ROW_DETAIL,
			timeout: 250,
			title: 'Il dettaglio di un acquisto',
		},
	];
}
